import { Table } from '@shape-ds/components';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSapNotesQuery } from 'queries/externalReferences/queries';
import { buildColumnsObject } from '@shape/lighthouse-components';
import { sapColumns } from 'components/ExternalReferencesTables/columsConfig';
import { sapNotesStatusMap } from 'components/SapNotesStatus/SapNoteStatus';
import StatusSummary from 'components/StatusSummary/StatusSummary';
import Filters from 'components/ExternalReferencesTables/Filters/Filters';
import { SapNote, SapNoteCriticalLevel } from 'queries/externalReferences/contract';
import { useFeatureFlag } from '@shape/feature-flag';
import TableDataContainer from './TableData.container';

interface MaintenanceModalProps {
  assetId: string;
  className?: string;
  showSearchText?: boolean;
  maxHeight?: number;
}

export const DEFAULT_PERIOD = '365';

const MaintenanceNotesTable: React.FC<MaintenanceModalProps> = ({
  assetId,
  className,
  showSearchText,
  maxHeight = 130,
}: MaintenanceModalProps) => {
  const [period, setPeriod] = useState<string>(DEFAULT_PERIOD);
  const isSapEnabled = useFeatureFlag('is-modec');
  const { data, isFetching } = useSapNotesQuery(assetId, period, {
    enabled: isSapEnabled && !!assetId,
  });
  const [filteredData, setFilteredData] = useState<SapNote[]>(data ?? []);
  const { t } = useTranslation();
  const columns = buildColumnsObject<SapNote>({
    columns: sapColumns,
    getLabel: (key: string) => t(`monitoring:tables.columns.SAP_NOTES.${key}`),
  });

  const handleStatusClick = (newActiveStatusList: string[]) => {
    if (!data) return;
    const newFilteredData = data?.filter((note) =>
      newActiveStatusList.includes(note.status as SapNoteCriticalLevel),
    );
    setActiveStatusList(newActiveStatusList as SapNoteCriticalLevel[]);
    setFilteredData(newActiveStatusList.length === 0 ? data : newFilteredData);
  };

  const [activeStatusList, setActiveStatusList] = useState<SapNoteCriticalLevel[]>([]);

  return (
    <TableDataContainer
      isLoading={isFetching}
      dataLength={data?.length}
      className={`maintenance-notes ${className}`}
      type="SAP_NOTES"
    >
      <StatusSummary
        data={data}
        variant="circle"
        criticityMap={sapNotesStatusMap}
        onStatusClick={handleStatusClick}
        activeList={activeStatusList}
      />
      <Filters<SapNote>
        data={data ?? []}
        onChangeFilters={(newData) => setFilteredData(newData)}
        setPeriod={setPeriod}
        period={period}
        searchKey={showSearchText ? ['description', 'notification_text'] : undefined}
        hasPreventiveFilter
      />
      {data && data.length > 0 && (
        <Table<SapNote>
          style={{ maxHeight }}
          className="table"
          columns={columns}
          data={filteredData}
        />
      )}
    </TableDataContainer>
  );
};

export default MaintenanceNotesTable;
